import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

// Only UI chrome is translated. Long-form editorial content (neighbourhood
// descriptions, articles) stays in English by design — those are curated
// artifacts that require expert French translation, not machine translation.
const DICT = {
    en: {
        nav: {
            neighbourhoods: 'Neighbourhoods',
            affordability: 'Affordability',
            invest: 'Investment',
            quiz: 'Lifestyle Match',
            compare: 'Compare',
            insights: 'Insights',
            signin: 'Sign in',
            signout: 'Sign out',
            dashboard: 'Dashboard',
            account: 'Account',
        },
        home: {
            eyebrow: 'Neighbourhood Intelligence · Southern Ontario',
            heroPre: 'The best neighbourhood decision you\'ll ever make',
            heroPost: 'starts with data.',
            heroBody: 'Independent research on where to live across Durham, Hamilton, Halton, Brant and Niagara. We combine schools, safety, walkability, affordability and appreciation into one honest score. No listings. No commissions. No noise.',
            ctaQuiz: 'Match my lifestyle',
            ctaExplore: 'Explore neighbourhoods',
            statNbhoods: 'Neighbourhoods',
            statRegions: 'Regions',
            statSignals: 'Signals per area',
            focusTitle: 'Nine markets. Deep coverage.',
        },
        common: {
            footerAbout: 'Southern Ontario\'s neighbourhood intelligence platform. Trusted data. Local knowledge. Financial insight.',
            weeklyBriefing: 'Get the weekly briefing',
            newsletterBody: 'Data, analysis, and one neighbourhood worth watching. No listings. No spam.',
            subscribe: 'Subscribe',
            focus: 'Focus markets',
            explore: 'Explore',
        },
        retiree: {
            title: 'The Retiree Migration Report',
            dek: 'Enter your current neighbourhood and home value. We\'ll build you a bespoke PDF report on the five Southern Ontario neighbourhoods worth downshifting to — with capital-freed and healthcare access included.',
            origin: 'Current neighbourhood',
            homeValue: 'Estimated home value (CAD)',
            email: 'Email (we send the PDF here)',
            submit: 'Generate my report',
            downloading: 'Preparing your report…',
        },
    },
    fr: {
        nav: {
            neighbourhoods: 'Quartiers',
            affordability: 'Abordabilité',
            invest: 'Investissement',
            quiz: 'Correspondance',
            compare: 'Comparer',
            insights: 'Analyses',
            signin: 'Connexion',
            signout: 'Déconnexion',
            dashboard: 'Tableau de bord',
            account: 'Compte',
        },
        home: {
            eyebrow: 'Intelligence des quartiers · Sud de l\'Ontario',
            heroPre: 'La meilleure décision de quartier commence',
            heroPost: 'avec des données.',
            heroBody: 'Recherche indépendante sur les quartiers de Durham, Hamilton, Halton, Brant et Niagara. Écoles, sécurité, marchabilité, abordabilité et appréciation — une seule note honnête. Aucune inscription. Aucune commission. Aucun bruit.',
            ctaQuiz: 'Trouver mon style de vie',
            ctaExplore: 'Explorer les quartiers',
            statNbhoods: 'Quartiers',
            statRegions: 'Régions',
            statSignals: 'Signaux par zone',
            focusTitle: 'Neuf marchés. Couverture approfondie.',
        },
        common: {
            footerAbout: 'La plateforme d\'intelligence des quartiers du Sud de l\'Ontario. Données fiables. Savoir local. Analyse financière.',
            weeklyBriefing: 'Recevoir le bulletin hebdomadaire',
            newsletterBody: 'Données, analyse et un quartier à surveiller. Aucune inscription. Aucun pourriel.',
            subscribe: 'S\'abonner',
            focus: 'Marchés ciblés',
            explore: 'Explorer',
        },
        retiree: {
            title: 'Rapport de migration à la retraite',
            dek: 'Entrez votre quartier actuel et la valeur de votre maison. Nous vous préparerons un rapport PDF sur les cinq meilleurs quartiers du Sud de l\'Ontario où déménager — avec capital libéré et accès aux soins de santé.',
            origin: 'Quartier actuel',
            homeValue: 'Valeur estimée de la maison (CAD)',
            email: 'Courriel (nous vous envoyons le PDF ici)',
            submit: 'Générer mon rapport',
            downloading: 'Préparation de votre rapport…',
        },
    },
};

const LangCtx = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        if (typeof window === 'undefined') return 'en';
        return window.localStorage.getItem('bn_lang') || 'en';
    });

    useEffect(() => {
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
    }, [lang]);

    const setLang = useCallback((l) => {
        setLangState(l);
        try { window.localStorage.setItem('bn_lang', l); } catch (_e) { /* ignore */ }
    }, []);

    // Deep key resolver: t('nav.neighbourhoods')
    const t = useCallback((key) => {
        const parts = key.split('.');
        let v = DICT[lang] || DICT.en;
        for (const p of parts) {
            v = v?.[p];
            if (v === undefined) break;
        }
        if (v === undefined) {
            // fallback to English
            let vv = DICT.en;
            for (const p of parts) vv = vv?.[p];
            return vv ?? key;
        }
        return v;
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
    return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
